/**
 * Joueurs (`GET /admin/players`) — **lecture seule**.
 *
 * A-26 est catégorique : la progression d'un joueur ne se modifie que par le jeu. Cet écran n'a
 * donc **aucun formulaire, aucun champ de saisie, aucun bouton de modification** : uniquement deux
 * boutons de pagination et un bouton d'actualisation. Les colonnes sont exactement celles que
 * l'API renvoie — l'email y est déjà masqué (`j***@exemple.invalid`).
 *
 * Un test de composant (`joueurs.spec.tsx`) vérifie l'absence de tout `input`, `textarea` et
 * `select` dans le rendu : c'est la garantie exécutable de cette règle.
 */
import { useState } from 'react';

import { listerJoueurs } from '../api/admin';
import { M } from '../catalogue/messages';
import { useChargement } from '../hooks/use-chargement';
import { BandeauErreur } from '../ui/Bandeau';
import { Bouton } from '../ui/Bouton';
import { Chargement } from '../ui/Chargement';
import { Coquille } from '../ui/Coquille';
import { Panneau } from '../ui/Panneau';
import { formaterDate } from '../utils/presentation';

/** Taille de page : la valeur par défaut du contrat, bien en deçà du plafond de 100. */
const PAR_PAGE = 20;

export function EcranJoueurs() {
  const [page, setPage] = useState(0);
  const chargement = useChargement(
    (signal) => listerJoueurs({ limit: PAR_PAGE, offset: page * PAR_PAGE }, signal),
    [page],
  );

  const joueurs = chargement.donnees?.players ?? [];
  const total = chargement.donnees?.total ?? 0;
  const nombreDePages = Math.max(1, Math.ceil(total / PAR_PAGE));

  return (
    <Coquille
      titre={M.joueurs.titre}
      sousTitre={M.joueurs.sousTitre}
      actions={
        <Bouton variante="secondaire" onClick={chargement.recharger}>
          {M.commun.actualiser}
        </Bouton>
      }
    >
      <Panneau aide={M.joueurs.lectureSeule}>
        {chargement.erreur === null ? null : (
          <BandeauErreur erreur={chargement.erreur} surReessai={chargement.recharger} />
        )}
        {chargement.chargement ? <Chargement texte={M.commun.chargementListe} /> : null}

        {chargement.chargement || chargement.erreur !== null ? null : (
          <>
            <table className="tableau">
              <caption className="tableau__legende">{M.commun.total(total)}</caption>
              <thead>
                <tr>
                  <th scope="col">{M.joueurs.colonnePseudo}</th>
                  <th scope="col">{M.joueurs.colonneEmail}</th>
                  <th scope="col">{M.joueurs.colonneRole}</th>
                  <th scope="col">{M.joueurs.colonneLocale}</th>
                  <th scope="col">{M.joueurs.colonneCreation}</th>
                  <th scope="col" className="tableau__nombre">
                    {M.joueurs.colonneNiveau}
                  </th>
                  <th scope="col" className="tableau__nombre">
                    {M.joueurs.colonneEclats}
                  </th>
                  <th scope="col" className="tableau__nombre">
                    {M.joueurs.colonnePoints}
                  </th>
                  <th scope="col">{M.joueurs.colonneSupprime}</th>
                </tr>
              </thead>
              <tbody>
                {joueurs.map((joueur) => (
                  <tr key={joueur.id}>
                    <td>{joueur.pseudo}</td>
                    <td>
                      <code>{joueur.emailMasque}</code>
                    </td>
                    <td>{joueur.role}</td>
                    <td>{joueur.locale}</td>
                    <td>{formaterDate(joueur.createdAt)}</td>
                    <td className="tableau__nombre">{joueur.niveauMax}</td>
                    <td className="tableau__nombre">{joueur.eclats}</td>
                    <td className="tableau__nombre">{joueur.pointsCollection}</td>
                    <td>
                      {joueur.deletedAt === null ? M.commun.vide : formaterDate(joueur.deletedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {joueurs.length === 0 ? <p className="etat-vide">{M.commun.aucunResultat}</p> : null}

            <div className="pagination">
              <Bouton
                variante="secondaire"
                onClick={() => {
                  setPage((courante) => Math.max(0, courante - 1));
                }}
                disabled={page === 0}
              >
                {M.commun.precedent}
              </Bouton>
              <span className="pagination__etat">{M.commun.page(page + 1, nombreDePages)}</span>
              <Bouton
                variante="secondaire"
                onClick={() => {
                  setPage((courante) => courante + 1);
                }}
                disabled={page + 1 >= nombreDePages}
              >
                {M.commun.suivant}
              </Bouton>
            </div>
          </>
        )}
      </Panneau>
    </Coquille>
  );
}
